import React, { useState } from 'react';
import { post } from '../../../services/ApiService';
import EscalationModal from './EscalationModal';
import EscalationActivityModal from './EscalationActivityModal';
import { ESCALATION_TYPES } from './escalationTypes';

// What the resolve / reopen modal shows and calls, per escalation type.
const STATUS_ACTIONS = {
  resolve: (config) => ({
    url: config.api.resolve,
    title: config.resolveTitle,
    placeholder: config.resolvePlaceholder,
    confirmLabel: config.resolveConfirmLabel,
    failMessage: 'Failed to resolve escalation',
  }),
  reopen: (config) => ({
    url: config.api.reopen,
    title: config.reopenTitle,
    placeholder: 'Reason for reopening (optional)',
    confirmLabel: 'Reopen Escalation',
    failMessage: 'Failed to reopen escalation',
  }),
};

const errorMessage = (error, fallback) => error.response?.data?.error || fallback;

// Resolve / reopen and replies on one escalation, shared by the escalation panels
// and the order detail page. Callers open a flow with the escalation and its order
// ref, render `modals`, and get the updated escalation back:
//   onStatusChange(escalation, note) after resolve / reopen (note = the order note)
//   onReply(escalation) after each reply
const useEscalationActions = ({ onStatusChange, onReply }) => {
  // { kind: 'resolve' | 'reopen', escalation, orderRef } while that modal is open.
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  // { escalation, orderRef } while the activity modal is open.
  const [activityTarget, setActivityTarget] = useState(null);
  const [replyLoading, setReplyLoading] = useState(false);

  const statusAction = statusTarget
    ? STATUS_ACTIONS[statusTarget.kind](ESCALATION_TYPES[statusTarget.escalation.type])
    : null;

  const runStatusAction = async (note) => {
    try {
      setStatusLoading(true);
      const res = await post(statusAction.url(statusTarget.escalation._id), { note });
      if (!res.success) return { success: false, message: res.error || statusAction.failMessage };
      onStatusChange(res.escalation, res.note);
      return { success: true, message: res.message };
    } catch (error) {
      return { success: false, message: errorMessage(error, statusAction.failMessage) };
    } finally {
      setStatusLoading(false);
    }
  };

  const sendReply = async (text) => {
    const { escalation } = activityTarget;
    try {
      setReplyLoading(true);
      const res = await post(ESCALATION_TYPES[escalation.type].api.reply(escalation._id), { text });
      if (!res.success) return { success: false, message: res.error };
      setActivityTarget((target) => ({ ...target, escalation: res.escalation }));
      onReply(res.escalation);
      return { success: true };
    } catch (error) {
      return { success: false, message: errorMessage(error, 'Failed to add reply') };
    } finally {
      setReplyLoading(false);
    }
  };

  const modals = (
    <>
      <EscalationModal
        open={!!statusTarget}
        loading={statusLoading}
        title={statusAction?.title || ''}
        subtitle={statusTarget?.orderRef}
        description={statusTarget?.escalation.reason ? `Escalation reason: ${statusTarget.escalation.reason}` : undefined}
        placeholder={statusAction?.placeholder}
        confirmLabel={statusAction?.confirmLabel || ''}
        onCancel={() => setStatusTarget(null)}
        onConfirm={runStatusAction}
      />
      <EscalationActivityModal
        open={!!activityTarget}
        escalation={activityTarget?.escalation}
        orderRef={activityTarget?.orderRef}
        loading={replyLoading}
        onCancel={() => setActivityTarget(null)}
        onReply={sendReply}
      />
    </>
  );

  return {
    openResolve: (escalation, orderRef) => setStatusTarget({ kind: 'resolve', escalation, orderRef }),
    openReopen: (escalation, orderRef) => setStatusTarget({ kind: 'reopen', escalation, orderRef }),
    openActivity: (escalation, orderRef) => setActivityTarget({ escalation, orderRef }),
    modals,
  };
};

export default useEscalationActions;
